import { Button } from "@mui/material";
import { GoogleMap, Marker, InfoWindow, useJsApiLoader } from "@react-google-maps/api";
import PropTypes from "prop-types";
import { useState, useCallback } from "react";

// lat: location.latitude,
// lng: location.longitude,
// label: location.plate,
// direction: location.direction,
// speed: location.speed,
// engineState: location.engineState,

const GoogleGps_Map = ({
  markers,
  width = "100%",
  height = "300px",
  markerSize = null,
  showNextPreviousButtons = false,
  nextPreviousTitle = "",
  selectedMarker: selectedMarkerProp = null,
}) => {
  const containerStyle = {
    width: width,
    height: height,
  };

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
  });

  // eslint-disable-next-line no-unused-vars
  const [map, setMap] = useState(null);
  const [mapCenter, setMapCenter] = useState(null);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [zoom, setZoom] = useState(15);

  const onLoad = useCallback(
    function callback(mapInstance) {
      setMap(mapInstance);
      if (markers && markers.length > 0) {
        const bounds = new window.google.maps.LatLngBounds();
        markers.forEach((marker) => {
          if (marker.lat && marker.lng) {
            bounds.extend({
              lat: parseFloat(marker.lat),
              lng: parseFloat(marker.lng),
            });
          }
        });
        if (markers.length === 1) {
          // For a single marker, set center and a default zoom
          mapInstance.setCenter({
            lat: parseFloat(markers[0].lat),
            lng: parseFloat(markers[0].lng),
          });
          setMapCenter({
            lat: parseFloat(markers[0].lat),
            lng: parseFloat(markers[0].lng),
          });
          mapInstance.setZoom(15);
        } else {
          mapInstance.fitBounds(bounds);
        }
      }
      if (selectedMarkerProp) {
        const selected = markers.find(
          (marker) => marker.lat === selectedMarkerProp.lat && marker.lng === selectedMarkerProp.lng
        );
        if (selected) {
          setSelectedMarker(selected);
          setMapCenter({
            lat: parseFloat(selected.lat),
            lng: parseFloat(selected.lng),
          });
          mapInstance.setZoom(15);
        }
      }
    },
    [markers]
  );

  const onUnmount = useCallback(function callback() {
    setMap(null);
  }, []);

  if (!markers) return null;
  // Validate markers before accessing markers[0]
  if (markers.length === 0 || !markers[0]?.lat || !markers[0]?.lng) return null;

  const MarkerDiv = ({ type, value }) => {
    return (
      <div className="spaceBetween">
        <div>{type}</div>
        <div>{value}</div>
      </div>
    );
  };
  MarkerDiv.propTypes = {
    type: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  };

  const getNextMarker = () => {
    if (!selectedMarker && markers.length > 0) return markers[0]; // Return the first marker if no marker is selected
    const currentIndex = markers.findIndex(
      (marker) => marker.lat === selectedMarker.lat && marker.lng === selectedMarker.lng
    );
    const nextIndex = (currentIndex + 1) % markers.length;
    setMapCenter({
      lat: parseFloat(markers[nextIndex].lat),
      lng: parseFloat(markers[nextIndex].lng),
    });
    setZoom(15);
    console.log(markers[nextIndex]);
    return markers[nextIndex];
  };

  const getPrevMarker = () => {
    if (!selectedMarker && markers.length > 0) return markers[markers.length - 1]; // Return the last marker if no marker is selected
    const currentIndex = markers.findIndex(
      (marker) => marker.lat === selectedMarker.lat && marker.lng === selectedMarker.lng
    );
    const prevIndex = (currentIndex - 1 + markers.length) % markers.length;
    setMapCenter({
      lat: parseFloat(markers[prevIndex].lat),
      lng: parseFloat(markers[prevIndex].lng),
    });
    setZoom(15);
    console.log(markers[prevIndex]);
    return markers[prevIndex];
  };

  return isLoaded ? (
    <>
      {showNextPreviousButtons && (
        <div className="spaceBetween">
          <Button
            size="small"
            variant="outlined"
            onClick={() => setSelectedMarker(getPrevMarker())}>
            Edellinen
          </Button>
          <div className="caption">{nextPreviousTitle}</div>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setSelectedMarker(getNextMarker())}>
            Seuraava
          </Button>
        </div>
      )}
      <GoogleMap
        mapContainerStyle={containerStyle}
        center={mapCenter}
        zoom={zoom}
        onLoad={onLoad}
        onUnmount={onUnmount}
        options={{
          disableDefaultUI: true,
          zoomControl: true,
          streetViewControl: true,
          mapTypeControl: true,
          fullscreenControl: true,
        }}
        onClick={() => setSelectedMarker(null)} // Close InfoWindow on map click
      >
        {markers.map((marker, index) => (
          <Marker
            key={marker.objectId || index} // Use objectId if available, otherwise use index
            position={{ lat: parseFloat(marker.lat), lng: parseFloat(marker.lng) }}
            label={{
              text: marker.label || " ",
              fontSize: "10px", // Adjust size as needed
              fontWeight: "bold",
            }}
            icon={
              marker.type === "tehdas"
                ? {
                    url: "data:image/svg+xml;utf-8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'><path d='M22 22H2V10.41l7-7V7h3v3h3v3h3v9z' fill='%231976d2'/></svg>",
                    scaledSize: new window.google.maps.Size(32, 32),
                  }
                : marker.type === "vehicle"
                ? {
                    path:
                      parseFloat(marker.speed) > 0
                        ? window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW
                        : window.google.maps.SymbolPath.CIRCLE,

                    scale: markerSize ? markerSize : parseFloat(marker.speed) > 0 ? 5 : 7,
                    fillColor: marker.engineState === "1" ? "green" : "red",
                    fillOpacity: 1,
                    strokeWeight: 1,
                    rotation: parseFloat(marker.direction) || 0,
                  }
                : undefined
            }
            onClick={() => {
              setSelectedMarker(marker);
            }}
          />
        ))}
        {selectedMarker && (
          <InfoWindow
            position={{ lat: parseFloat(selectedMarker.lat), lng: parseFloat(selectedMarker.lng) }}
            onCloseClick={() => setSelectedMarker(null)}>
            <div style={{ color: "black" }}>
              {!selectedMarker.isStopData && (
                <div className="spaceBetween">
                  <strong>{selectedMarker.label}</strong>
                  {selectedMarker.type}
                </div>
              )}
              {selectedMarker.isStopData && (
                <div>
                  Pysähdys nro: {selectedMarker.label}
                  <br />
                  {selectedMarker.startTime.split("+")[0]}
                  <br />
                  {selectedMarker.endTime.split("+")[0]}
                  <div>
                    <b>{selectedMarker.durationMinutes} min</b>
                  </div>
                </div>
              )}
              <MarkerDiv value={selectedMarker.address} />
              {(selectedMarker.speed > 0 || selectedMarker.engineState === "1") && (
                <MarkerDiv type="Nopeus" value={selectedMarker.speed + " km/h"} />
              )}
              {selectedMarker.speed > 0 && (
                <MarkerDiv type="Suunta" value={selectedMarker.direction + "°"} />
              )}
              {!selectedMarker.isStopData && selectedMarker.speed === 0 && (
                <>
                  <MarkerDiv
                    type="Moottori"
                    value={selectedMarker.engineState === "1" ? "Käynnissä" : "Sammutettu"}
                  />
                  {/* <MarkerDiv type="Pysähtyi klo" value={selectedMarker.timestamp.split("+")[0]} /> */}
                </>
              )}
              {selectedMarker.engineState === "0" && (
                <MarkerDiv type="Sammutettu klo" value={selectedMarker.lastEngineOnTime} />
              )}
            </div>
          </InfoWindow>
        )}
      </GoogleMap>
    </>
  ) : (
    <></>
  );
};

export default GoogleGps_Map;

GoogleGps_Map.propTypes = {
  markers: PropTypes.array,
  width: PropTypes.string,
  height: PropTypes.string,
  markerSize: PropTypes.number,
  showNextPreviousButtons: PropTypes.bool,
  nextPreviousTitle: PropTypes.string,
  selectedMarker: PropTypes.object,
};
